import { getHttpErrorStatus } from './receiptsApi';

const RECEIPT_REQUEST_ERROR_TYPE = 'RECEIPT_REQUEST_ERROR';

export type ReceiptRequestErrorPayload = {
  type: typeof RECEIPT_REQUEST_ERROR_TYPE;
  ambiguous: boolean;
  message: string;
  status?: number;
  /** A 409 create whose document id is reserved by a deleted receipt; retrying cannot succeed. */
  documentReserved?: boolean;
};

const isReceiptRequestErrorPayload = (error: unknown): error is ReceiptRequestErrorPayload =>
  typeof error === 'object' &&
  error !== null &&
  'type' in error &&
  error.type === RECEIPT_REQUEST_ERROR_TYPE &&
  'ambiguous' in error &&
  typeof error.ambiguous === 'boolean';

export const toReceiptRequestErrorPayload = (error: unknown): ReceiptRequestErrorPayload => {
  const status = getHttpErrorStatus(error);
  return {
    type: RECEIPT_REQUEST_ERROR_TYPE,
    ambiguous: status === undefined || status === 408 || status >= 500,
    message: error instanceof Error ? error.message : 'Receipt request failed.',
    ...(status !== undefined ? { status } : {}),
  };
};

/**
 * Returns whether a failed receipt request may have reached the service despite the client error.
 * Network failures, timeouts, and server errors require reconciliation; definitive client errors do not.
 */
export const isAmbiguousReceiptRequestError = (error: unknown): boolean => {
  if (isReceiptRequestErrorPayload(error)) {
    return error.ambiguous;
  }

  const status = getHttpErrorStatus(error);
  return status === undefined || status === 408 || status >= 500;
};
